// components/dashboard/findMeATenant/ListingCriteriaStep.jsx
// Content from Tenant_Access_Listing_Criteria_Before_You_Submit_Your_Listing.docx

import React, { useState } from "react";
import {
  Box, Typography, Alert, Paper, List, ListItem,
  ListItemIcon, ListItemText, Button, Divider, Checkbox,
  FormControlLabel,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const DECLINE_REASONS = [
  {
    title: "The Property Is Not Show-Ready",
    body: "Properties must be clean, safe, and presentable for prospective tenant tours. Properties with excessive clutter, significant cleanliness issues, major deferred maintenance, or obvious safety concerns may be declined until corrections are made.",
  },
  {
    title: "Professional Photos Are Not Available",
    body: "Listings should include clear, marketing-quality photos that accurately represent the property and highlight its best features. Photos are often the first impression prospective tenants have of a property.",
  },
  {
    title: "The Property Is Significantly Overpriced",
    body: "Asking rent should not exceed our estimated fair market rent by more than approximately 5%. The first two weeks of a listing are often the most important — listings that launch significantly above market can lose momentum and become more difficult to lease later.",
  },
  {
    title: "The Showing Environment Is Not Conducive To Leasing",
    body: "Examples include hostile occupants, aggressive pets, unsafe access conditions, repeated showing restrictions, or conditions that create a poor experience for prospective tenants. Successful leasing requires a positive and cooperative showing experience.",
  },
  {
    title: "The Property Is Unlikely To Meet Our 14-Day Leasing Standard",
    body: "We carefully evaluate every listing before acceptance. If we believe a property is unlikely to achieve a successful outcome without changes, we may recommend improvements before moving forward.",
  },
];

/**
 * ListingCriteriaStep
 *
 * Step 0 of the Find Me A Tenant flow (before property info is collected).
 * Shows the program eligibility criteria so owners know what's required.
 *
 * Props:
 *   onAccept {func} - called when user confirms they've read criteria and wish to continue
 *   onBack   {func} - optional
 */
const ListingCriteriaStep = ({ onAccept, onBack }) => {
  const [acknowledged, setAcknowledged] = useState(false);

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
        Before You Submit Your Listing
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Our 14-Day Vacancy Fill Program works best when properties are
        positioned for success. Please review the criteria below before
        continuing.
      </Typography>

      <Alert
        severity="info"
        icon={<CheckCircleOutlineIcon />}
        sx={{ mb: 2.5, borderRadius: 2 }}
      >
        At Tenant Access, our goal is to help secure qualified tenants within{" "}
        <strong>14 days</strong> whenever possible — not simply to advertise
        properties. Because of this commitment, not every property is a fit
        for our program.
      </Alert>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
        Your Listing May Be Declined If:
      </Typography>

      <List disablePadding>
        {DECLINE_REASONS.map(({ title, body }) => (
          <Paper
            key={title}
            variant="outlined"
            sx={{ mb: 1.5, borderRadius: 2, overflow: "hidden" }}
          >
            <ListItem alignItems="flex-start" sx={{ py: 1.5 }}>
              <ListItemIcon sx={{ mt: 0.5, minWidth: 36 }}>
                <WarningAmberIcon color="warning" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={700}>
                    {title}
                  </Typography>
                }
                secondary={
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {body}
                  </Typography>
                }
              />
            </ListItem>
          </Paper>
        ))}
      </List>

      <Alert severity="success" sx={{ mt: 1, mb: 3, borderRadius: 2 }}>
        <Typography variant="body2">
          <strong>We succeed when your property leases quickly.</strong> By
          accepting only properly positioned listings, we maintain service
          quality and maximize the likelihood of securing qualified tenants
          fast. If your property requires adjustments, we'll explain why and
          provide recommendations.
        </Typography>
      </Alert>

      <FormControlLabel
        control={
          <Checkbox
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            I have read and understand the listing criteria. My property is
            show-ready, competitively priced, and I'm ready to proceed.
          </Typography>
        }
        sx={{ mb: 1 }}
      />

      <Divider sx={{ my: 2 }} />

      <Box display="flex" justifyContent="space-between">
        {onBack ? (
          <Button onClick={onBack} sx={{ textTransform: "none" }}>
            Back
          </Button>
        ) : (
          <span />
        )}
        <Button
          variant="contained"
          onClick={onAccept}
          disabled={!acknowledged}
          sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
        >
          Continue — Submit Property Info
        </Button>
      </Box>
    </Box>
  );
};

export default ListingCriteriaStep;
